import { useQuery } from '@tanstack/react-query';

import { useOrganization } from '@/contexts/OrganizationContext';
import {
  getFeatureAccessCatalogEntry,
  type FeatureAccessSource,
  type KnownFeatureAccessKey,
} from '@/lib/featureAccessCatalog';
import { invokeProtectedFunction } from '@/lib/protectedFunctionAuth';
import type { FeatureReleaseStage } from '@/components/ui/feature-stage-badge';

export type ResolvedFeatureAccess = {
  key: KnownFeatureAccessKey;
  enabled: boolean;
  releaseStage?: FeatureReleaseStage;
  source: FeatureAccessSource;
  reason:
    | 'disabled'
    | 'enabled_all_users'
    | 'enabled_for_user'
    | 'enabled_for_org'
    | 'enabled_all_organizations'
    | 'feature_assigned'
    | 'feature_subscription_active'
    | 'included_in_paid_subscription'
    | 'feature_unassigned'
    | 'unknown_feature';
};

type CustomerFeaturesResolveResponse = {
  features: ResolvedFeatureAccess[];
};

type FeatureFlagsResolveResponse = {
  flags: Array<Omit<ResolvedFeatureAccess, 'source'> & { key: KnownFeatureAccessKey }>;
};

type UseFeatureAccessOptions = {
  organizationId?: string | null;
  // Disambiguates a key registered under more than one source (an exposure flag AND
  // an entitlement). Pass it to fail-closed resolve such a key; omit it for the
  // vast majority of keys, which live under a single source.
  source?: FeatureAccessSource;
  // ENT-5889: callers of an organisation-targeted flag pass false while no organisation is known,
  // so no unscoped request (refused 400 organization_id_required) is ever sent.
  enabled?: boolean;
};

async function fetchFeatureAccess(
  key: KnownFeatureAccessKey,
  organizationId: string | null,
  userId: string,
  source?: FeatureAccessSource,
): Promise<ResolvedFeatureAccess> {
  const entry = getFeatureAccessCatalogEntry(key, source);
  if (!entry) {
    return {
      key,
      enabled: false,
      source: 'customer_feature',
      reason: 'unknown_feature',
      releaseStage: null,
    };
  }

  if (entry.source === 'beta_feature') {
    const { data, error } = await invokeProtectedFunction<FeatureFlagsResolveResponse>(
      'feature-flags-resolve',
      { keys: [key], organizationId },
      { expectedUserId: userId },
    );

    if (error) {
      throw error;
    }

    const flags = (data as FeatureFlagsResolveResponse | null)?.flags ?? [];
    const resolvedFlag = flags[0];

    return {
      key,
      enabled: resolvedFlag?.enabled ?? false,
      source: 'beta_feature',
      reason: resolvedFlag?.reason ?? 'disabled',
      releaseStage: resolvedFlag?.releaseStage ?? null,
    };
  }

  const { data, error } = await invokeProtectedFunction<CustomerFeaturesResolveResponse>(
    'customer-features-resolve',
    {
      keys: [key],
      organizationId,
    },
    { expectedUserId: userId },
  );

  if (error) {
    throw error;
  }

  const features = (data as CustomerFeaturesResolveResponse | null)?.features ?? [];
  return features[0] ?? {
    key,
    enabled: false,
    source: 'customer_feature',
    reason: 'unknown_feature',
    releaseStage: null,
  };
}

export function useFeatureAccess(
  key: KnownFeatureAccessKey,
  options: UseFeatureAccessOptions = {},
) {
  const { organization, user } = useOrganization();
  const entry = getFeatureAccessCatalogEntry(key, options.source);
  const organizationId = Object.prototype.hasOwnProperty.call(options, 'organizationId')
    ? options.organizationId ?? null
    : organization?.id ?? null;

  const query = useQuery({
    queryKey: ['feature-access', key, entry?.source ?? options.source ?? null, organizationId, user?.id ?? 'anonymous'],
    queryFn: () => fetchFeatureAccess(key, organizationId, user!.id, options.source),
    enabled: options.enabled !== false && Boolean(key) && Boolean(entry) && Boolean(user?.id) && (
      entry?.source === 'beta_feature' || Boolean(organizationId)
    ),
    staleTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    access: query.data,
    enabled: query.data?.enabled ?? false,
  };
}
